import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Archive,
  Database,
  RefreshCw,
  Scale,
  Server,
} from "lucide-react";
import { getOpsSummary, type OpsSummary } from "@/api/endpoints/ops";

const SEVERITY_BADGE: Record<string, string> = {
  info: "bg-sky-600/20 text-sky-300",
  warning: "bg-amber-600/20 text-amber-300",
  critical: "bg-red-600/20 text-red-300",
};

const REPLICA_STATUS_BADGE: Record<string, string> = {
  unsatisfied: "bg-orange-600/20 text-orange-300",
  stale: "bg-amber-600/20 text-amber-300",
  failed: "bg-red-600/20 text-red-300",
  syncing: "bg-sky-600/20 text-sky-300",
  pending: "bg-amber-600/20 text-amber-300",
  up_to_date: "bg-emerald-600/20 text-emerald-300",
};

export default function Ops() {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["ops"],
    queryFn: getOpsSummary,
    refetchInterval: 30_000,
  });

  if (error) {
    const msg = (error as Error).message;
    return (
      <div className="rounded-lg border border-red-700/40 bg-red-700/10 p-4 text-red-300">
        Failed to load ops summary: {msg}
        {msg.includes("403") && (
          <p className="mt-1 text-sm text-red-200">
            This page is admin-only.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Operations</h1>
          <p className="mt-1 text-sm text-gray-400">
            Aggregated health snapshot — replicas, alerts, backups, LB.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-200 hover:bg-gray-700"
        >
          <RefreshCw className={"h-4 w-4 " + (isFetching ? "animate-spin" : "")} />
          Refresh
        </button>
      </div>

      {isLoading && <div className="text-gray-400">Loading…</div>}
      {data && <OpsCards data={data} />}
    </div>
  );
}

function OpsCards({ data }: { data: OpsSummary }) {
  const replicaAttention = data.replicas.needs_attention_count;
  const replicaTotal = Object.values(data.replicas.by_status).reduce(
    (a, b) => a + b,
    0,
  );
  const activeAlerts = Object.values(
    data.alerts.by_status_severity.active ?? {},
  ).reduce((a, b) => a + b, 0);
  const failedBackups24h = data.backups.last_24h.failed ?? 0;
  const completedBackups24h = data.backups.last_24h.completed ?? 0;

  return (
    <>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Stat
          icon={<Server className="h-5 w-5 text-blue-400" />}
          label="VMs"
          value={data.vms.total}
          tone="neutral"
        />
        <Stat
          icon={<Database className="h-5 w-5 text-yellow-400" />}
          label="Replicas needing attention"
          value={`${replicaAttention} / ${replicaTotal}`}
          tone={replicaAttention > 0 ? "warn" : "ok"}
        />
        <Stat
          icon={<AlertTriangle className="h-5 w-5 text-red-400" />}
          label="Active alerts"
          value={activeAlerts}
          tone={activeAlerts > 0 ? "warn" : "ok"}
        />
        <Stat
          icon={<Archive className="h-5 w-5 text-emerald-400" />}
          label="Backups last 24 h"
          value={`${completedBackups24h} OK / ${failedBackups24h} KO`}
          tone={failedBackups24h > 0 ? "warn" : "ok"}
        />
        <Stat
          icon={<Scale className="h-5 w-5 text-purple-400" />}
          label="LB pools (enabled)"
          value={`${data.lb.enabled_pools} pools · ${data.lb.enabled_backends} backends`}
          tone="neutral"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel title="VMs by tier and status">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-gray-500">
              <tr>
                <th className="px-2 py-1 text-left">Tier</th>
                <th className="px-2 py-1 text-left">Status</th>
                <th className="px-2 py-1 text-right">Count</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {Object.entries(data.vms.by_tier_status).flatMap(([tier, statuses]) =>
                Object.entries(statuses).map(([st, cnt]) => (
                  <tr key={`${tier}-${st}`}>
                    <td className="px-2 py-1 font-mono text-gray-300">{tier}</td>
                    <td className="px-2 py-1 text-gray-300">{st}</td>
                    <td className="px-2 py-1 text-right text-gray-200">{cnt}</td>
                  </tr>
                )),
              )}
              {Object.keys(data.vms.by_tier_status).length === 0 && (
                <EmptyRow colSpan={3} />
              )}
            </tbody>
          </table>
        </Panel>

        <Panel title="Replicas needing attention">
          {data.replicas.needs_attention.length === 0 ? (
            <p className="text-sm text-gray-500">All replicas healthy.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-2 py-1 text-left">VM</th>
                  <th className="px-2 py-1 text-left">Node</th>
                  <th className="px-2 py-1 text-left">Status</th>
                  <th className="px-2 py-1 text-left">Last sync</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {data.replicas.needs_attention.map((r) => (
                  <tr key={r.id}>
                    <td className="px-2 py-1 text-gray-300">#{r.vm_id}</td>
                    <td className="px-2 py-1 text-gray-400">{r.replica_node || "—"}</td>
                    <td className="px-2 py-1">
                      <span
                        className={`rounded px-1.5 py-0.5 text-xs ${REPLICA_STATUS_BADGE[r.status] ?? ""}`}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="px-2 py-1 text-xs text-gray-500">
                      {r.last_sync_at
                        ? new Date(r.last_sync_at).toLocaleString()
                        : "never"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel title="Recent active alerts">
          {data.alerts.active_recent.length === 0 ? (
            <p className="text-sm text-gray-500">No active alerts.</p>
          ) : (
            <ul className="space-y-2">
              {data.alerts.active_recent.map((a) => (
                <li
                  key={a.id}
                  className="flex items-start justify-between gap-2 rounded border border-gray-800 px-3 py-2 text-sm"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`rounded px-1.5 py-0.5 text-xs ${SEVERITY_BADGE[a.severity] ?? ""}`}
                      >
                        {a.severity}
                      </span>
                      <span className="text-gray-200">{a.title}</span>
                    </div>
                    {a.source && (
                      <div className="mt-1 text-xs text-gray-500">{a.source}</div>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(a.created_at).toLocaleString()}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Schedules with last run failed">
          {data.backups.failed_schedules.length === 0 ? (
            <p className="text-sm text-gray-500">No failed schedule.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-2 py-1 text-left">VM</th>
                  <th className="px-2 py-1 text-left">Cron</th>
                  <th className="px-2 py-1 text-left">Last run</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {data.backups.failed_schedules.map((s) => (
                  <tr key={s.id}>
                    <td className="px-2 py-1 text-gray-300">#{s.vm_id}</td>
                    <td className="px-2 py-1 font-mono text-xs text-gray-400">{s.cron_expr}</td>
                    <td className="px-2 py-1 text-xs text-gray-500">
                      {s.last_run_at
                        ? new Date(s.last_run_at).toLocaleString()
                        : "never"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      </div>

      <p className="text-right text-xs text-gray-600">
        Snapshot: {new Date(data.generated_at).toLocaleString()}
      </p>
    </>
  );
}

function Stat({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  tone: "ok" | "warn" | "neutral";
}) {
  const border = {
    ok: "border-emerald-700/30",
    warn: "border-amber-700/40",
    neutral: "border-gray-800",
  }[tone];
  return (
    <div className={`rounded-lg border ${border} bg-gray-900 p-4`}>
      <div className="flex items-center gap-2">{icon}<span className="text-sm text-gray-400">{label}</span></div>
      <div className="mt-2 text-2xl font-semibold text-gray-100">{value}</div>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
      <h2 className="mb-3 font-semibold text-gray-100">{title}</h2>
      {children}
    </div>
  );
}

function EmptyRow({ colSpan }: { colSpan: number }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-2 py-3 text-center text-sm text-gray-500">
        No data.
      </td>
    </tr>
  );
}
